import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { FilesList } from './FilesList';
import { StatusHeader } from './StatusHeader';
import { StatusMessage } from './StatusMessage';
import { useSubscription } from '@/Domain/components/SubscriptionProvider';
import { UploadStatusEnum } from '@/Domain/enum/UploadStatus.enum';
import { useArticleFileStatus } from '@/Domain/hooks/KnowledgeBase/useArticleFileStatus.hook';
import { useArticleStateChangeSocket } from '@/Domain/socket/KnowledgeBase/useArticleStateChange.socket';
import {
  useUploadStore,
  type ScrapingProgress,
  type UploadFile,
} from '@/Domain/store/upload.store';

// A file is "in flight" while still being uploaded, parsed, or scraped — any
// status that isn't terminal (DONE / DONE_FALLBACK / ERROR / META_ARTICLE_DELETED).
const IN_FLIGHT_STATUSES = [
  UploadStatusEnum.PENDING,
  UploadStatusEnum.PARSING,
  UploadStatusEnum.PARSING_FALLBACK,
] as const;

/**
 * Pick the upload file that owns the active scrape session. Drives both the
 * displayed scrapingProgress and the per-session `key` on StatusMessage. The
 * key (the file id) resets StatusMessage's monotonic-clamp and animation refs
 * the instant a new scrape takes over — without it those per-instance refs
 * persist on the single mounted StatusMessage and freeze the next scrape's
 * bar/headline at the previous scrape's high-water mark.
 *
 * Two-stage lookup:
 *   1. If any file is in-flight, scope strictly to in-flight files: prefer the
 *      one that already has scrapingProgress (preserves the PR 3783 guard — a
 *      lingering completed scrape must not shadow a freshly started one), else
 *      the first in-flight file so the key is stable from the "Starting…"
 *      frame, before any webhook has set scrapingProgress.
 *   2. If everything is terminal, fall back to the most recent terminal file
 *      with progress. Without this a META_ARTICLE_DELETED carrying
 *      `cappedAtLimit=true` is dropped the moment the file flips PENDING→DONE
 *      (useArticleStateChange.socket.ts writes both in the same handler) and
 *      the success view falls to the plain "Adding Knowledge successful!"
 *      branch instead of the upgrade CTA. The capped pill is exempt from
 *      auto-hide so the inline UpgradePlanButton stays until dismissed.
 */
export const pickActiveFile = (files: UploadFile[]): UploadFile | undefined => {
  const inFlight = files.filter((f) =>
    (IN_FLIGHT_STATUSES as readonly UploadStatusEnum[]).includes(f.status),
  );
  if (inFlight.length) return inFlight.find((f) => f.scrapingProgress) ?? inFlight[0];
  for (let i = files.length - 1; i >= 0; i--) {
    if (files[i].scrapingProgress) return files[i];
  }
  return undefined;
};

export const pickActiveScrapingProgress = (files: UploadFile[]): ScrapingProgress | undefined =>
  pickActiveFile(files)?.scrapingProgress;

/**
 * Pull the most recently known errorMessage off the latest upload file so
 * mixed-success cases (Multiple Pages: "Added 3 of 5 — 2 failed") can
 * surface the message on the terminal success pill. Scans back-to-front
 * so the freshest submit wins.
 */
export const pickLatestErrorMessage = (files: UploadFile[]): string | undefined => {
  for (let i = files.length - 1; i >= 0; i--) {
    if (files[i].errorMessage) return files[i].errorMessage;
  }
  return undefined;
};

const ONE_MINUTE = 1000 * 60;
// Hold the "Adding Knowledge successful" pill on screen long enough for the
// user to register it, then collapse just that pill (not the whole widget).
// Values chosen so the dismiss feels intentional but doesn't overstay.
const SUCCESS_AUTOHIDE_DELAY_MS = 3500;
export const StatusUploadFiles = () => {
  const { files, isMinimized, toggleMinimize, isOpen, setIsOpen, resetStore } = useUploadStore();

  const [isCloseButtonVisible, setIsCloseButtonVisible] = useState(false);
  const [componentVisible, setComponentVisible] = useState(true);
  // Flips to true `SUCCESS_AUTOHIDE_DELAY_MS` after a terminal success.
  // Drives a grid-rows collapse on the success-pill region — the rest of the
  // widget (header, tabs, file list) stays mounted and interactive, and the
  // content above animates smoothly into the space the pill used to occupy
  // rather than snapping down once the pill unmounts.
  const [isSuccessPillHidden, setIsSuccessPillHidden] = useState(false);

  useArticleStateChangeSocket({});

  const { successMessage, hasPendingFiles, hasErrorFiles, getFileIcon, getFileSizeText } =
    useArticleFileStatus(files);

  // Sourced from /allowed_features (the same query useKbUsageSocket keeps
  // fresh under bursts), so the in-flight at-limit hint flips on within
  // ~400ms of the user crossing their plan's articles_limit mid-scrape.
  const { getConfig, isFetched } = useSubscription();
  const kbAtLimit = isFetched && getConfig('can_add_article') === false;

  const activeFile = pickActiveFile(files);
  const rawActiveProgress = activeFile?.scrapingProgress;
  // Layer the upload-store `cappedAtLimit` flag with the live subscription
  // state so a successful upgrade collapses the warning pill back to the
  // normal success branch (which auto-hides). Without this AND, the flag
  // is sticky on the file forever — a user who clicked "Upgrade Plan" from
  // the pill, paid, and returned would still see "Your Knowledge Base is
  // full" with the upgrade CTA, which is exactly the wrong message for
  // someone who just paid us.
  const activeScrapingProgress =
    rawActiveProgress && rawActiveProgress.cappedAtLimit && !kbAtLimit
      ? { ...rawActiveProgress, cappedAtLimit: false }
      : rawActiveProgress;
  const isCappedAtLimit = activeScrapingProgress?.cappedAtLimit === true;
  const latestErrorMessage = pickLatestErrorMessage(files);
  // First in-flight URL upload — surfaced to the StatusMessage so the widget
  // can show "Starting… — example.com" the instant the user submits, before
  // any backend webhook has had a chance to fire. Same scoping as
  // activeScrapingProgress so a prior completed URL doesn't win.
  const pendingUrl = files.find(
    (f) => f.url && (IN_FLIGHT_STATUSES as readonly UploadStatusEnum[]).includes(f.status),
  )?.url;

  useEffect(() => {
    if (!isOpen && files.length === 0) {
      resetStore();
    }
  }, [isOpen, files.length, resetStore]);

  useEffect(() => {
    if (files.length > 0) {
      setIsOpen(true);
      setComponentVisible(true);
    }
  }, [files, setIsOpen]);

  useEffect(() => {
    let timer: NodeJS.Timeout;

    if (isOpen) {
      timer = setTimeout(() => setIsCloseButtonVisible(true), ONE_MINUTE);
    }

    return () => clearTimeout(timer);
  }, [isOpen]);

  const handleCloseModal = () => {
    setComponentVisible(false);
    setIsOpen(false);
    resetStore();
  };

  // Auto-hide just the success pill on terminal success — NOT the whole
  // widget. After `SUCCESS_AUTOHIDE_DELAY_MS`, flip `isSuccessPillHidden`,
  // which collapses the pill's container via a `grid-template-rows: 0fr`
  // animation; the header, tabs, and file list stay mounted. Errors and
  // capped-at-limit terminal pills keep their pill up indefinitely: errors
  // so the user sees the failure, capped so the inline UpgradePlanButton
  // stays available until they dismiss the widget. If a new upload lands
  // mid-countdown the deps flip and the timer is cleared.
  useEffect(() => {
    const isTerminalSuccess = files.length > 0 && !hasPendingFiles && !hasErrorFiles;
    if (!isTerminalSuccess || isCappedAtLimit) {
      setIsSuccessPillHidden(false);
      return;
    }

    const hideTimer = setTimeout(() => setIsSuccessPillHidden(true), SUCCESS_AUTOHIDE_DELAY_MS);
    return () => clearTimeout(hideTimer);
  }, [files.length, hasPendingFiles, hasErrorFiles, isCappedAtLimit]);

  if (files.length === 0 || !isOpen || !componentVisible) {
    return null;
  }

  return (
    <div
      className={classNames(
        'fixed bottom-4 right-4 bg-card border border-border rounded-lg shadow-lg z-40 overflow-hidden',
        { 'w-96': !isMinimized },
      )}
    >
      <div
        className={classNames({
          'bg-card rounded-lg shadow-lg w-96 overflow-hidden': isMinimized,
        })}
      >
        <StatusHeader
          isMinimized={isMinimized}
          toggleMinimize={toggleMinimize}
          closeModal={handleCloseModal}
          isCloseButtonVisible={!hasPendingFiles || isCloseButtonVisible}
        />

        {!isMinimized && (
          <div className="px-4 mt-3 max-h-64 overflow-y-auto custom-scroll">
            <FilesList files={files} getFileIcon={getFileIcon} getFileSizeText={getFileSizeText} />
          </div>
        )}

        {/* `grid-rows-[Nfr]` animates the track height between its content
            size and 0, which — paired with `overflow-hidden` on the child —
            lets us collapse the success pill without a layout snap when the
            pill unmounts. Tailwind arbitrary values keep the trick CSS-only
            so we don't need a measured height.

            Layout note: the bottom 12px spacer below the track is permanent
            (`h-3`). Inside the track we use `p-3 pb-0` so the pill still
            has full padding on all sides while visible (the spacer supplies
            the bottom 12px). When the row collapses to 0fr the inner
            padding vanishes with it, but the outer spacer keeps a steady
            gap between the file list and the widget bottom instead of the
            list slamming against the edge. */}
        <div
          data-testid="status-message-wrapper"
          className={classNames(
            'grid transition-[grid-template-rows,opacity] duration-500 ease-out',
            isSuccessPillHidden ? 'grid-rows-[0fr] opacity-0' : 'grid-rows-[1fr] opacity-100',
          )}
          aria-hidden={isSuccessPillHidden}
        >
          <div className="overflow-hidden">
            <div className="p-3 pb-0">
              <StatusMessage
                key={activeFile?.id ?? 'idle'}
                successMessage={successMessage}
                hasPendingFiles={hasPendingFiles}
                hasErrorFiles={hasErrorFiles}
                scrapingProgress={activeScrapingProgress}
                pendingUrl={pendingUrl}
                partialFailureMessage={latestErrorMessage}
                kbAtLimit={kbAtLimit}
              />
            </div>
          </div>
        </div>
        <div aria-hidden className="h-3" />
      </div>
    </div>
  );
};
